import useMissionAcceptanceReview from '../hooks/useMissionAcceptanceReview';
import useMissionPanelScroll from '../hooks/useMissionPanelScroll';
import { missionReviewContent } from '../helpers/scenarioReview';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import {
  faChevronLeft,
  faCalendarClock,
  faClipboardList,
  faExpand,
  faList,
  faMapLocationDot,
  faPhone,
  faEnvelope,
  faStore,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';

import {
  AcceptMissionStatus,
  AcceptRemoteMissionRequest,
  GetRemoteMissionDetailsResponse,
  GetRemoteMissionEligibilityResponse,
  MissionConfigurationDetailType,
  MissionsClient,
  RemoteMissionActivity,
  RemoteMissionAllocation,
  RemoteMissionBranchChoiceDto,
  RemoteMissionSummaryDto,
} from '../api/api.remote.g';
import AuthGuard from '../components/common/AuthGuard';
import CustomButton from '../components/common/CustomButton';
import CustomMarkdown from '../components/common/CustomMarkDown';
import Label, { LabelType } from '../components/common/Label';
import Loading from '../components/common/Loading';
import RelatedMinorSelectionPopup from '../components/common/mission/RelatedMinorSelectionPopup';
import MissionDetailSections from '../components/common/mission/MissionDetailSections';
import MissionCompensation from '../components/common/mission/MissionCompensation';
import { MissionPurchaseDetails } from '../components/common/mission/MissionPurchaseConditions';
import AtHomeContactActions from '../components/common/mission/AtHomeContactActions';
import MissionPromotion from '../components/common/mission/MissionPromotion';
import MissionBasicInformationPopup from '../components/common/mission/MissionBasicInformationPopup';
import MissionAcceptanceNotice from '../components/common/mission/MissionAcceptanceNotice';
import RemoteMissionsIntroduction from '../components/common/mission/RemoteMissionsIntroduction';
import ProfileManager, { ProfileManagerHandles } from '../components/myaccount/myprofile/ProfileManager';
import { hasMissionBasicInformation } from '../models/helpers/missionBasicInformation';
import MissionListLogo from '../components/mymission/MissionListLogo';
import NavigationButton from '../components/common/NavigationButton';
import MapFilter from '../components/common/filter/MapFilter';
import useApi from '../hooks/api-hook';
import { useAppDispatch, useAppSelector } from '../hooks/store-hook';
import useCustomTheme from '../hooks/theme-hook';
import useTranslation from '../hooks/translation-hook';
import useStyles from '../hooks/styles-hook';
import { lightBoxActions } from '../store/lightBoxSlice';
import { missionsActions } from '../store/missionsSlice';
import { map as RemotePosMap } from '../models/helpers/platformImportHelper';
import type { MapMarker } from '../models/MapModels';
import {
  filterAndSortRemoteMissions,
  filterRemoteBranchesForShopper,
  getRemoteBranchMapViewport,
  hasRemoteBranchCoordinates,
  isRemoteBranchSelectable,
  remoteMissionVisibilityDefaults,
  requiresRemotePhoneCall,
  type RemoteActivityFilter,
} from '../models/helpers/remoteMissions';
import { faCircleChevronLeft } from '@fortawesome/free-solid-svg-icons';

const remoteReviewContent = (detail?: GetRemoteMissionDetailsResponse) => missionReviewContent(
  detail?.summary?.name ?? '', detail?.details, detail?.scenarioUpdateNotice, {
    subtitle: detail?.summary?.brandName,
    logoUrl: detail?.summary?.hasLogo ? detail.summary.logoUrl : undefined,
    salaryPoints: detail?.summary?.salaryPoints,
    pointsOnly: true,
    promotionDisplay: detail?.summary?.promotionDisplay,
  },
);

const RemoteMissions = () => {
  const t = useTranslation();
  // The Remote v2 client is generated from the coordinated PortalCore contract in a
  // separate module to preserve compatibility with existing mission flows.
  const api = useApi(MissionsClient as any) as unknown as MissionsClient;
  const authStore = useAppSelector((state) => state.auth);
  const missionsStore = useAppSelector((state) => state.missions);
  const userPrefs = useAppSelector((state) => state.userPrefs);
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const theme = useCustomTheme();
  const styles = useStyles((section) => section.myMissions);
  const radioStyles = useStyles((section) => section.radioButton);
  const { width, height } = useWindowDimensions();

  const [focused, setFocused] = useState(false);
  const [activityFilter, setActivityFilter] = useState<RemoteActivityFilter>('all');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [onlyAcceptableMissions, setOnlyAcceptableMissions] = useState(remoteMissionVisibilityDefaults.onlyAcceptableMissions);
  const [showUpcomingMissions, setShowUpcomingMissions] = useState(remoteMissionVisibilityDefaults.showUpcomingMissions);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedMissionId, setSelectedMissionId] = useState<string>();
  const [selectedBranchId, setSelectedBranchId] = useState<string>();
  const [branchPickerView, setBranchPickerView] = useState<'list' | 'map'>('list');
  const [mapExpanded, setMapExpanded] = useState(false);
  const [acceptanceStatus, setAcceptanceStatus] = useState<AcceptMissionStatus>();
  const [showScenario, setShowScenario] = useState(false);
  const [profileDialogVisible, setProfileDialogVisible] = useState(false);
  const profileManager = useRef<ProfileManagerHandles>(null);
  const acceptanceContext = useRef<string | undefined>(undefined);
  const detailsScroll = useRef<ScrollView>(null);
  const contentOffset = useRef(0);
  const scenarioOffset = useRef(0);
  const scrollToScenario = useCallback(() => {
    detailsScroll.current?.scrollTo({ y: contentOffset.current + scenarioOffset.current, animated: false });
  }, []);
  const openScenario = useCallback(() => { setShowScenario(true); scrollToScenario(); }, [scrollToScenario]);
  const acceptanceReview = useMissionAcceptanceReview(`${authStore.user?.uid}:${selectedMissionId}:${selectedBranchId}:${focused}`,
    openScenario, { presentation: 'footer', onReadInstructions: openScenario });
  const detailsScrolling = useMissionPanelScroll(detailsScroll, false, `${authStore.user?.uid}:${selectedMissionId}`);
  useEffect(() => {
    if (!showScenario) return;
    const timer = setTimeout(scrollToScenario, 0);
    return () => clearTimeout(timer);
  }, [showScenario, scrollToScenario]);
  // A profile dialog must not resume acceptance for a different mission/POS or a closed page.
  const profileContext = `${selectedMissionId ?? ''}:${selectedBranchId ?? ''}`;
  acceptanceContext.current = focused ? profileContext : undefined;

  useEffect(() => () => { acceptanceContext.current = undefined; }, []);

  useEffect(() => { setProfileDialogVisible(false); }, [focused, profileContext]);

  const isCompact = width < 768;
  const useDesktopAcceptanceFooter = Platform.OS === 'web' && !isCompact;
  const useAcceptanceFooter = useDesktopAcceptanceFooter || acceptanceReview.isOpen;
  const canExpandMap = Platform.OS === 'web' && !isCompact;

  useEffect(() => {
    if (!canExpandMap || !focused) setMapExpanded(false);
  }, [canExpandMap, focused]);

  useFocusEffect(
    React.useCallback(() => {
      setFocused(true);
      return () => setFocused(false);
    }, []),
  );

  const missionsQuery = useQuery({
    enabled: focused && !!authStore.idToken && !!missionsStore.missionListSeed,
    queryKey: ['remoteMissions', authStore.idToken, missionsStore.missionListSeed],
    queryFn: ({ signal }) => {
      const promise = api.getRemoteMissions(undefined, api.tokenSource?.token);
      signal?.addEventListener('abort', () => {
        api.tokenSource?.cancel('Remote missions request was cancelled.');
        api.onCancel();
      });
      return promise;
    },
  });

  const allMissions = missionsQuery.data ?? [];
  const availableTags = useMemo(() => {
    const tags = new Map<string, string>();
    allMissions.forEach((mission) => mission.tags?.forEach((tag) => tag.id && tags.set(tag.id, tag.name ?? '')));
    return Array.from(tags, ([id, name]) => ({ id, name, checked: selectedTagIds.includes(id) })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allMissions, selectedTagIds]);

  const visibleMissions = useMemo(() => {
    return filterAndSortRemoteMissions(allMissions, {
      activity: activityFilter,
      selectedTagIds,
      onlyAcceptableMissions,
      showUpcomingMissions,
      surveyActivity: RemoteMissionActivity.SurveyOnly,
      missionActivity: RemoteMissionActivity.ActivityAndSurvey,
    });
  }, [activityFilter, allMissions, onlyAcceptableMissions, selectedTagIds, showUpcomingMissions]);

  useEffect(() => {
    if (selectedMissionId && visibleMissions.some((mission) => mission.periodMissionConfigurationId === selectedMissionId)) return;
    setSelectedMissionId(!isCompact ? visibleMissions[0]?.periodMissionConfigurationId : undefined);
  }, [isCompact, selectedMissionId, visibleMissions]);

  const selectedMission = allMissions.find((mission) => mission.periodMissionConfigurationId === selectedMissionId);
  const detailsQuery = useQuery({
    enabled: !!selectedMissionId && !!authStore.idToken,
    queryKey: ['remoteMission', selectedMissionId, missionsStore.missionListSeed],
    queryFn: () => api.getRemoteMissionDetails(selectedMissionId!, api.tokenSource?.token),
  });

  useEffect(() => {
    setSelectedBranchId(undefined);
    setBranchPickerView('list');
    setMapExpanded(false);
    setAcceptanceStatus(undefined);
    setShowScenario(false);
  }, [selectedMissionId]);

  const isPool = selectedMission?.allocation === RemoteMissionAllocation.SharedPool;
  const eligibilityQuery = useQuery({
    enabled: !!selectedMissionId && !selectedMission?.upcomingPeriod && (isPool || !!selectedBranchId),
    queryKey: ['remoteMissionEligibility', selectedMissionId, selectedBranchId, missionsStore.missionListSeed],
    queryFn: () => api.getRemoteMissionEligibility(selectedMissionId!, selectedBranchId, api.tokenSource?.token),
  });

  useEffect(() => {
    setAcceptanceStatus(eligibilityQuery.data?.status);
  }, [eligibilityQuery.data]);

  const refreshAvailability = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['remoteMissions'] }),
      queryClient.invalidateQueries({ queryKey: ['remoteMission', selectedMissionId] }),
      queryClient.invalidateQueries({ queryKey: ['remoteMissionEligibility', selectedMissionId] }),
    ]);
  };

  const showMinorSelection = (eligibility: GetRemoteMissionEligibilityResponse) => {
    dispatch(
      lightBoxActions.setContent(
        <RelatedMinorSelectionPopup
          minors={eligibility.eligibleRelatedMinors}
          onConfirm={(selectedRelatedMinorId) => {
            dispatch(lightBoxActions.setVisibility(false));
            acceptMutation.mutate({ selectedRelatedMinorId });
          }}
        />,
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const acceptMutation = useMutation({
    mutationFn: async (attempt: { selectedRelatedMinorId?: string; scenarioReviewCompleted?: boolean }) => {
      const { selectedRelatedMinorId } = attempt;
      const eligibility = await api.getRemoteMissionEligibility(selectedMissionId!, selectedBranchId, api.tokenSource?.token);
      if (!eligibility.canAccept) return { eligibility };
      if (eligibility.requiresRelatedMinorSelection && !selectedRelatedMinorId) return { eligibility };

      if (!attempt.scenarioReviewCompleted) {
        const detail = detailsQuery.data;
        acceptanceReview.request(remoteReviewContent(detail), async () => {
          const latest = await detailsQuery.refetch();
          if (latest.error || !latest.data) throw latest.error ?? new Error('Mission unavailable');
          return remoteReviewContent(latest.data);
        }, () => acceptMutation.mutate({ ...attempt, scenarioReviewCompleted: true }));
        return { eligibility, reviewPending: true };
      }
      const request = new AcceptRemoteMissionRequest();
      request.scenarioReviewCompleted = true;
      request.branchId = isPool ? undefined : selectedBranchId;
      request.selectedRelatedMinorId = selectedRelatedMinorId;
      const response = await api.acceptRemoteMission(selectedMissionId!, request, api.tokenSource?.token);
      return { eligibility, response };
    },
    onSuccess: async ({ eligibility, response, reviewPending }) => {
      if (reviewPending) return;
      if (response?.acceptMissionStatus === AcceptMissionStatus.ScenarioReviewRequired) {
        await detailsQuery.refetch();
        acceptMutation.mutate({ selectedRelatedMinorId: acceptMutation.variables?.selectedRelatedMinorId });
        return;
      }
      if (!response && eligibility.requiresRelatedMinorSelection && eligibility.eligibleRelatedMinors?.length) {
        showMinorSelection(eligibility);
        return;
      }

      if (response?.success) {
        dispatch(missionsActions.setMissionAcceptedId(response.missionAssignationId));
        dispatch(missionsActions.setReloadMissionListSeed(`${Date.now()}`));
        navigation.navigate({ name: 'mymissions', params: response.missionAssignationId ? { id: response.missionAssignationId } : undefined } as never);
        return;
      }

      setAcceptanceStatus(response?.acceptMissionStatus ?? eligibility.status);
      await refreshAvailability();
    },
    onError: refreshAvailability,
  });

  const statusText = (status?: AcceptMissionStatus) => {
    switch (status) {
      case AcceptMissionStatus.MissionUnavailable:
      case AcceptMissionStatus.Retry:
        return t('remote-v2-availability-changed');
      case AcceptMissionStatus.MissionAlreadyInProgress:
        return t('MissionAlreadyInProgress');
      case AcceptMissionStatus.IncompleteProfileRequirements:
        return t('remote-v2-profile-incomplete');
      case AcceptMissionStatus.UserIneligibleForMission:
      case AcceptMissionStatus.MissionLimitRotationReach:
        return t('remote-v2-not-eligible');
      case AcceptMissionStatus.MissionLimitReached:
        return t('remote-v2-limit-reached');
      default:
        return undefined;
    }
  };

  const selectMission = (mission: RemoteMissionSummaryDto) => setSelectedMissionId(mission.periodMissionConfigurationId);
  const visibleBranches = useMemo(
    () => filterRemoteBranchesForShopper(
      detailsQuery.data?.branches ?? [],
      requiresRemotePhoneCall(detailsQuery.data?.summary?.requiresPhoneCall),
    ),
    [detailsQuery.data?.branches, detailsQuery.data?.summary?.requiresPhoneCall],
  );
  const selectedBranch = visibleBranches.find((branch) => branch.branchId === selectedBranchId);
  const canAccept =
    !!selectedMissionId &&
    !selectedMission?.upcomingPeriod &&
    (isPool || !!selectedBranch) &&
    !selectedBranch?.availabilityBlocked &&
    !acceptMutation.isLoading &&
    !eligibilityQuery.isFetching &&
    (eligibilityQuery.data?.canAccept === true ||
      (!hasMissionBasicInformation(userPrefs) && eligibilityQuery.data?.status === AcceptMissionStatus.IncompleteProfileRequirements));

  const requestMissionAcceptance = () => {
    if (!canAccept) return;
    if (hasMissionBasicInformation(userPrefs)) {
      acceptMutation.mutate({});
      return;
    }

    const pendingContext = acceptanceContext.current;
    let resumed = false;
    const resumeAcceptance = (updatedProfile = userPrefs) => {
      if (resumed || acceptanceContext.current !== pendingContext || !hasMissionBasicInformation(updatedProfile)) return;
      resumed = true;
      // The mutation performs a fresh server eligibility check after the profile has been saved.
      acceptMutation.mutate({});
    };
    dispatch(lightBoxActions.setContent(
      <MissionBasicInformationPopup
        onClose={() => dispatch(lightBoxActions.setVisibility(false))}
        onConfirm={() => {
          dispatch(lightBoxActions.setVisibility(false));
          if (acceptanceContext.current !== pendingContext) return;
          if (profileManager.current?.verifyUserCanAcceptMission(userPrefs, resumeAcceptance, { suspendDrawer: false })) {
            resumeAcceptance();
          }
        }}
      />,
    ));
    dispatch(lightBoxActions.setVisibility(true));
  };
  const scenarioText =
    detailsQuery.data?.details?.find((section) => section.type === MissionConfigurationDetailType.Scenario)?.text ?? '';

  const defaultLogo = useMemo(
    () => <FontAwesomeIcon icon={faStore} style={styles.newVersion.list.logo.icon} />,
    [styles.newVersion.list.logo.icon],
  );

  const list = (
    <View style={styles.container.sideBar}>
      {missionsQuery.isLoading ? (
        <Loading style={{ height: 200 }} text={t('remote-v2-loading')} />
      ) : (
        <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ paddingBottom: 16, flexGrow: 1 }}>
          <View style={{ paddingHorizontal: 12, paddingBottom: 12 }}>
            <Label variant={LabelType.titleLargeNoCapsPrimary} style={styles.newVersion.list.status}>
              {t('remote-v2-title')}
            </Label>
            <MapFilter
              inlineMode
              inlinePanelInFlow
              items={availableTags}
              selectedFilterType={activityFilter === 'survey' ? 1 : activityFilter === 'mission' ? 2 : 0}
              showMissionTypeFilters
              missionTypeOptions={[
                { value: 0, label: t('remote-v2-all') },
                { value: 1, label: t('remote-v2-surveys'), icon: faClipboardList },
                { value: 2, label: t('at-home-missions-filter'), icon: faList },
              ]}
              onlyAcceptableMissions={onlyAcceptableMissions}
              showUpcomingMissions={showUpcomingMissions}
              unavailableToggleLabel={t('show-unavailable-short')}
              upcomingToggleLabel={t('show-upcoming-short')}
              open={filtersOpen}
              onOpenChange={setFiltersOpen}
              onSelectionChange={setSelectedTagIds}
              onFilterTypeChange={(next) => setActivityFilter(next === 1 ? 'survey' : next === 2 ? 'mission' : 'all')}
              onOnlyAcceptableMissions={(next) => setOnlyAcceptableMissions(!!next)}
              onShowUpcomingMissions={(next) => setShowUpcomingMissions(!!next)}
            />
          </View>
          {visibleMissions.map((mission) => {
            const selected = mission.periodMissionConfigurationId === selectedMissionId;
            const activity =
              mission.activity === RemoteMissionActivity.ActivityAndSurvey
                ? t('at-home-activity-and-survey')
                : t('remote-v2-survey-only');

            return (
              <Pressable
                key={mission.periodMissionConfigurationId}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                onPress={() => selectMission(mission)}
                style={[styles.newVersion.list.item, selected ? styles.newVersion.list.itemSelected : undefined]}
              >
                <View style={styles.newVersion.list.logoBase}>
                  <MissionListLogo
                    logoUrl={mission.hasLogo ? mission.logoUrl : undefined}
                    defaultLogo={defaultLogo}
                    imageStyle={styles.newVersion.list.logo.image}
                  />
                </View>
                <View style={{ flexShrink: 1 }}>
                  <Label
                    numberOfLines={2}
                    style={{ color: theme.text.primary, fontSize: 16, textTransform: 'uppercase', fontWeight: '600' }}
                  >
                    {mission.name}
                  </Label>
                  <Label numberOfLines={2} style={{ color: theme.text.primary, fontSize: 13, fontWeight: '600', marginBottom: 4 }}>
                    {mission.brandName}
                  </Label>
                  <Label style={{ color: theme.text.titleLight, fontSize: 12, fontWeight: '600', marginBottom: 3 }}>
                    {activity}
                  </Label>
                  {requiresRemotePhoneCall(mission.requiresPhoneCall) && (
                    <Label numberOfLines={0} style={{ color: theme.text.titleLight, fontSize: 12 }}>{t('at-home-phone-call-required')}</Label>
                  )}
                  {mission.requiresEmail && <Label numberOfLines={0} style={{ color: theme.text.titleLight, fontSize: 12 }}>{t('at-home-email-required')}</Label>}
                  <MissionPromotion display={mission.promotionDisplay} />
                  {mission.upcomingPeriod && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 3 }}>
                      <FontAwesomeIcon icon={faCalendarClock} size={12} color={theme.indicatorColor.toString()} />
                      <Label style={{ color: theme.indicatorColor, fontSize: 12, fontWeight: '600', marginLeft: 5 }}>
                        {t('starting-on')} {mission.upcomingPeriodStartDate?.toLocaleDateString()}
                      </Label>
                    </View>
                  )}
                </View>
              </Pressable>
            );
          })}
          {!visibleMissions.length && (
            <View style={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', padding: 32 }}>
              <FontAwesomeIcon icon={faClipboardList} size={32} color={theme.borderColor.toString()} />
              <Label style={{ textAlign: 'center', marginTop: 12 }}>{t('remote-v2-empty')}</Label>
            </View>
          )}
        </ScrollView>
      )}
    </View>
  );

  const remoteBranchMarkers = useMemo<MapMarker[]>(
    () => visibleBranches
      .filter((branch) => !!branch.branchId && hasRemoteBranchCoordinates(branch))
      .map((branch) => {
        const selectable = isRemoteBranchSelectable(branch);
        return {
          id: branch.branchId!,
          title: branch.name,
          accessibilityLabel: branch.name,
          coordinate: { latitude: branch.latitude!, longitude: branch.longitude! },
          faIcon: faStore,
          selected: branch.branchId === selectedBranchId,
          defaultPin: selectable ? 'default' : 'grey',
          seed: `remote-pos-${branch.branchId}-${selectable ? 'available' : 'unavailable'}-${branch.latitude}-${branch.longitude}`,
        };
      }),
    [selectedBranchId, visibleBranches],
  );

  const remoteMapViewport = useMemo(() => getRemoteBranchMapViewport(visibleBranches), [visibleBranches]);
  const remoteMapViewportCommand = useMemo(() => {
    if (!remoteMapViewport) return undefined;
    const id = remoteBranchMarkers.reduce((hash, marker) => {
      const markerKey = `${marker.id}:${marker.coordinate.latitude.toFixed(5)}:${marker.coordinate.longitude.toFixed(5)}`;
      return markerKey.split('').reduce((next, character) => ((next * 31) + character.charCodeAt(0)) >>> 0, hash);
    }, 17);

    return remoteMapViewport.count === 1
      ? { id, type: 'centerZoom' as const, center: remoteMapViewport.center, zoom: 12, suppressBoundsCallback: true }
      : {
          id,
          type: 'fitBounds' as const,
          northEast: remoteMapViewport.northEast,
          southWest: remoteMapViewport.southWest,
          edgePadding: { top: 44, right: 44, bottom: 44, left: 44 },
          maxZoom: 12,
          suppressBoundsCallback: true,
        };
  }, [remoteBranchMarkers, remoteMapViewport]);

  // Reserve the same space for short and wrapped entries, including required contacts.
  // A minimum (not fixed) height still lets long text and accessibility text sizes grow.
  const branchChoiceMinHeight = visibleBranches.some((branch) =>
    (detailsQuery.data?.summary?.requiresPhoneCall === true && !!branch.phone) ||
    (detailsQuery.data?.summary?.requiresEmail === true && !!branch.email),
  ) ? 140 : 112;
  const branchChoice = (branch: RemoteMissionBranchChoiceDto, lastItem = true) => {
    const selected = branch.branchId === selectedBranchId;
    const selectable = isRemoteBranchSelectable(branch);
    return (
      <Pressable
        key={branch.branchId}
        accessibilityRole="radio"
        accessibilityState={{ disabled: !selectable, checked: selected }}
        disabled={!selectable}
        onPress={() => setSelectedBranchId(branch.branchId)}
        style={({ pressed }) => [
          styles.newVersion.list.item,
          {
            borderBottomWidth: lastItem ? 0 : 1,
            backgroundColor: theme.background.missionDetail,
            minHeight: branchChoiceMinHeight,
            alignItems: 'center',
            opacity: !selectable ? 0.55 : pressed ? 0.75 : 1,
          },
          selected && { backgroundColor: theme.background.missionDetailSummary },
        ]}
      >
        <View style={[styles.newVersion.list.logoBase, { width: 32, height: 32, borderRadius: 16, marginRight: 10, flexShrink: 0 }]}>
          <FontAwesomeIcon icon={faStore} size={16} color={theme.text.titleLight.toString()} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Label variant={LabelType.titleSmall} style={{ lineHeight: 19 }}>{branch.name}</Label>
          <Label variant={LabelType.body1} style={{ marginTop: 4, lineHeight: 18 }}>
            {[branch.address, branch.city].filter(Boolean).join(', ')}
          </Label>
          {detailsQuery.data?.summary?.requiresPhoneCall === true && !!branch.phone && (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
              <FontAwesomeIcon icon={faPhone} size={12} color={theme.text.titleLight.toString()} />
              <Label variant={LabelType.body1} style={{ marginLeft: 6, flexShrink: 1 }}>{branch.phone}</Label>
            </View>
          )}
          {detailsQuery.data?.summary?.requiresEmail === true && !!branch.email && (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
              <FontAwesomeIcon icon={faEnvelope} size={12} color={theme.text.titleLight.toString()} />
              <Label numberOfLines={0} variant={LabelType.body1} style={{ marginLeft: 6, flexShrink: 1 }}>{branch.email}</Label>
            </View>
          )}
          {branch.availabilityBlocked && (
            <Label style={{ color: theme.text.error, marginTop: 4 }}>
              {t(branch.availabilityBlockReason === 'MissingEmail' ? 'remote-v2-email-required' : 'remote-v2-phone-required')}
            </Label>
          )}
        </View>
        <View accessible={false} style={[radioStyles.base, { marginLeft: 10, flexShrink: 0 }, selected && radioStyles.checked]}>
          {selected && <View style={radioStyles.marker} />}
        </View>
      </Pressable>
    );
  };

  const renderPosMap = () => remoteMapViewport ? (
    <RemotePosMap
      mapId="missions-map"
      disableClustering
      hidePanToOrigin
      showUserLocation={false}
      referencePosition={remoteMapViewport.center}
      markerItems={remoteBranchMarkers}
      selectedId={selectedBranchId}
      viewportCommand={remoteMapViewportCommand}
      onCleanSelection={() => setSelectedBranchId(undefined)}
      markerClick={(branchId) => {
        const branch = visibleBranches.find((item) => item.branchId === branchId);
        if (!branch || !isRemoteBranchSelectable(branch)) return false;
        setSelectedBranchId(branchId);
        return true;
      }}
    />
  ) : (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24 }}>
      <Label style={{ textAlign: 'center' }}>{t('remote-v2-map-empty')}</Label>
    </View>
  );

  const acceptanceActions = (
    <>
      {!!statusText(acceptanceStatus) && (
        <MissionAcceptanceNotice
          message={statusText(acceptanceStatus)!}
          style={useDesktopAcceptanceFooter
            ? { flexBasis: 320, flexGrow: 1, flexShrink: 1, width: 'auto', paddingHorizontal: 0 }
            : { marginBottom: 12 }}
        />
      )}
      {acceptanceStatus !== AcceptMissionStatus.MissionAlreadyInProgress && (
        <CustomButton
          testID="remote-mission-accept"
          disabled={!canAccept}
          title={acceptMutation.isLoading ? t('remote-v2-accepting') : t('accept_the_mission')}
          onPress={requestMissionAcceptance}
          style={isCompact ? { width: '100%', flexShrink: 0 } : { width: 240, alignSelf: useDesktopAcceptanceFooter ? 'flex-end' : 'center', flexShrink: 0 }}
        />
      )}
    </>
  );

  const detail = !selectedMissionId ? null : detailsQuery.isLoading ? (
    <View style={{ flexGrow: 1 }}>
      <Loading style={{ height: 200 }} />
    </View>
  ) : (
    <View style={[styles.newVersion.detailsContainer, useAcceptanceFooter && { gap: 0, minHeight: 0, minWidth: 0 }]}>
      <ScrollView
        testID="remote-mission-details-page" ref={detailsScroll} onScroll={detailsScrolling.onScroll} scrollEventThrottle={16}
        style={[styles.newVersion.detailsWrapper, useAcceptanceFooter && { flex: 1, minHeight: 0 }]}
        contentContainerStyle={{ paddingBottom: useAcceptanceFooter ? 24 : isCompact ? 96 : 32 }}
      >
        <View style={styles.newVersion.details.header.container}>
          <View style={styles.newVersion.list.logoBase}>
            <MissionListLogo
              logoUrl={detailsQuery.data?.summary?.hasLogo ? detailsQuery.data.summary.logoUrl : undefined}
              defaultLogo={defaultLogo}
              imageStyle={styles.newVersion.list.logo.image}
            />
          </View>
          <View style={styles.newVersion.details.header.title}>
            <Label
              numberOfLines={2}
              style={{ color: theme.text.primary, fontSize: 16, textTransform: 'uppercase', fontWeight: '600', flexShrink: 1 }}
            >
              {detailsQuery.data?.summary?.name}
            </Label>
            <Label numberOfLines={2} style={{ color: theme.text.primary, fontSize: 13, fontWeight: '600', marginBottom: 4, flexShrink: 1 }}>
              {detailsQuery.data?.summary?.brandName}
            </Label>
            <Label style={{ color: theme.text.titleLight, fontSize: 12, fontWeight: '600' }}>
              {t('remote-v2-title')} ·{' '}
              {detailsQuery.data?.summary?.activity === RemoteMissionActivity.ActivityAndSurvey
                ? t('at-home-activity-and-survey')
                : t('remote-v2-survey-only')}
            </Label>
            {requiresRemotePhoneCall(detailsQuery.data?.summary?.requiresPhoneCall) && (
              <Label numberOfLines={0} style={{ color: theme.text.titleLight, fontSize: 12 }}>{t('at-home-phone-call-required')}</Label>
            )}
            {detailsQuery.data?.summary?.requiresEmail && (
              <Label numberOfLines={0} style={{ color: theme.text.titleLight, fontSize: 12 }}>{t('at-home-email-required')}</Label>
            )}
            {detailsQuery.data?.summary?.upcomingPeriod && (
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                <FontAwesomeIcon icon={faCalendarClock} size={12} color={theme.indicatorColor.toString()} />
                <Label style={{ color: theme.indicatorColor, fontSize: 12, fontWeight: '600', marginLeft: 5 }}>
                  {t('starting-on')} {detailsQuery.data.summary.upcomingPeriodStartDate?.toLocaleDateString()}
                </Label>
              </View>
            )}
          </View>
        </View>

        <View style={styles.newVersion.details.content.container} onLayout={event => {
          contentOffset.current = event.nativeEvent.layout.y;
        }}>
          <View style={styles.newVersion.details.content.leftContainer}>
            {detailsQuery.data && (
              <>
                <MissionCompensation compensation={detailsQuery.data.summary ?? {}} purchaseConditions={detailsQuery.data} pointsOnly>
                  <AtHomeContactActions
                    contacts={detailsQuery.data.branches?.find(branch => branch.branchId === selectedBranchId) ?? detailsQuery.data}
                    requirements={detailsQuery.data.summary ?? {}}
                  />
                </MissionCompensation>
                <MissionPurchaseDetails conditions={detailsQuery.data} />
              </>
            )}
            {detailsQuery.data?.summary?.allocation === RemoteMissionAllocation.PerBranch && (
              <View>
                <Label variant={LabelType.title}>{t('remote-v2-choose-pos')}</Label>
                <View
                  accessibilityRole="tablist"
                  style={{
                    flexDirection: 'row',
                    borderWidth: 1,
                    borderColor: theme.borderColorLight,
                    borderRadius: 8,
                    overflow: 'hidden',
                    marginTop: 10,
                    marginBottom: 10,
                  }}
                >
                  {([
                    { value: 'list' as const, label: t('remote-v2-list-view'), icon: faList },
                    { value: 'map' as const, label: t('remote-v2-map-view'), icon: faMapLocationDot },
                  ]).map((option) => {
                    const selected = branchPickerView === option.value;
                    return (
                      <Pressable
                        key={option.value}
                        accessibilityRole="tab"
                        accessibilityState={{ selected }}
                        onPress={() => setBranchPickerView(option.value)}
                        style={{
                          flex: 1,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          paddingVertical: 9,
                          backgroundColor: selected ? theme.background.sea : theme.background.primary,
                        }}
                      >
                        <FontAwesomeIcon icon={option.icon} size={14} color={(selected ? theme.text.inverse : theme.text.primary).toString()} />
                        <Label style={{ marginLeft: 7, color: selected ? theme.text.inverse : theme.text.primary, fontWeight: '600' }}>
                          {option.label}
                        </Label>
                      </Pressable>
                    );
                  })}
                </View>
                {branchPickerView === 'list' ? (
                  <>
                    <View accessibilityRole="radiogroup" accessibilityLabel={t('remote-v2-choose-pos')} style={{ borderRadius: 8, overflow: 'hidden' }}>
                      {visibleBranches.map((branch, index) => branchChoice(branch, index === visibleBranches.length - 1))}
                    </View>
                    {!visibleBranches.length && <Label>{t('remote-v2-no-pos')}</Label>}
                  </>
                ) : remoteMapViewport ? (
                  <>
                    <View
                      style={{
                        width: '100%',
                        height: isCompact ? 280 : 320,
                        borderWidth: 1,
                        borderColor: theme.borderColorLight,
                        borderRadius: 8,
                        overflow: 'hidden',
                      }}
                    >
                      {!mapExpanded && renderPosMap()}
                      {canExpandMap && (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={t('remote-v2-expand-map')}
                          onPress={() => setMapExpanded(true)}
                          style={{
                            position: 'absolute', top: 10, right: 10,
                            flexDirection: 'row', alignItems: 'center',
                            paddingHorizontal: 12, minHeight: 40,
                            borderRadius: 8, borderWidth: 1, borderColor: theme.borderColorLight,
                            backgroundColor: theme.background.primary,
                          }}
                        >
                          <FontAwesomeIcon icon={faExpand} size={16} color={theme.text.primary.toString()} />
                          <Label style={{ marginLeft: 8, fontWeight: '600' }}>{t('remote-v2-expand-map')}</Label>
                        </Pressable>
                      )}
                    </View>
                    <Label style={{ color: theme.text.titleLight, marginVertical: 8 }}>{t('remote-v2-map-select')}</Label>
                    {selectedBranch && <View style={{ borderRadius: 8, overflow: 'hidden' }}>{branchChoice(selectedBranch)}</View>}
                  </>
                ) : (
                  <Label>{t('remote-v2-map-empty')}</Label>
                )}
              </View>
            )}
          </View>

          <View style={styles.newVersion.details.content.rightContainer} onLayout={event => {
            scenarioOffset.current = event.nativeEvent.layout.y;
            if (showScenario) scrollToScenario();
          }}>
            <View style={styles.newVersion.details.content.details.container}>
              {showScenario ? (
                <>
                  <Label variant={LabelType.title} style={styles.newVersion.details.header.subTitle}>
                    {t('scenario')}
                  </Label>
                  <CustomMarkdown>{scenarioText || missionReviewContent('', detailsQuery.data?.details).instructions}</CustomMarkdown>
                  <NavigationButton
                    style={[styles.newVersion.details.content.details.infoBlocks.baseLinks, { marginTop: 20 }]}
                    icon={faChevronLeft}
                    iconSize={16}
                    color={theme.text.titleLight}
                    labelVariant={LabelType.titleSmall}
                    onPress={() => { acceptanceReview.close(); setShowScenario(false); }}
                    text={t('back')}
                  />
                </>
              ) : (
                <MissionDetailSections
                  details={detailsQuery.data?.details}
                  scenarioUpdateNotice={detailsQuery.data?.scenarioUpdateNotice}
                  promotionDisplay={detailsQuery.data?.summary?.promotionDisplay}
                  resetKey={selectedMissionId}
                  onOpenScenario={openScenario}
                />
              )}

              {!useAcceptanceFooter && acceptanceActions}
            </View>
          </View>
        </View>
      </ScrollView>
      {useAcceptanceFooter && (
        <View
          testID="remote-mission-acceptance-footer"
          style={{
            flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end',
            flexShrink: 0, gap: 12, paddingHorizontal: 16, paddingVertical: 12,
            borderTopWidth: 1, borderTopColor: theme.borderColorLight,
            backgroundColor: theme.background.primary,
          }}
        >
          {acceptanceReview.isOpen ? acceptanceReview.element : acceptanceActions}
        </View>
      )}
    </View>
  );

  return (
    <AuthGuard>
      {!!authStore.idToken && !!authStore.user?.uid && (
        <RemoteMissionsIntroduction
          key={authStore.user.uid}
          blocked={filtersOpen || mapExpanded || profileDialogVisible || acceptMutation.isLoading || acceptanceReview.isOpen}
        />
      )}
      <ProfileManager key={focused ? profileContext : 'inactive'} ref={profileManager} onVisibilityChange={setProfileDialogVisible} />
      {canExpandMap && mapExpanded && (
        <Modal transparent visible animationType="fade" onRequestClose={() => setMapExpanded(false)}>
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <Pressable
              accessible={false}
              onPress={() => setMapExpanded(false)}
              style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
            />
            <View
              accessibilityViewIsModal
              accessibilityLabel={t('remote-v2-choose-pos')}
              style={{
                width: Math.min(width - 48, 1320), height: Math.min(height - 48, 860),
                backgroundColor: theme.background.primary, borderRadius: 12, overflow: 'hidden',
                borderWidth: 1, borderColor: theme.borderColorLight,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderColor: theme.borderColorLight }}>
                <View style={{ flex: 1, marginRight: 16 }}>
                  <Label variant={LabelType.title}>{t('remote-v2-choose-pos')}</Label>
                  <Label numberOfLines={1} style={{ color: theme.text.titleLight, marginTop: 4 }}>{selectedMission?.name}</Label>
                </View>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('remote-v2-close-map')}
                  onPress={() => setMapExpanded(false)}
                  style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center', borderRadius: 8, backgroundColor: theme.background.secondary }}
                >
                  <FontAwesomeIcon icon={faXmark} size={20} color={theme.text.primary.toString()} />
                </Pressable>
              </View>
              <View style={{ flex: 1, minHeight: 0, flexDirection: 'row' }}>
                <View style={{ flex: 1, minWidth: 0 }}>{renderPosMap()}</View>
                <View style={{ width: width < 1000 ? 280 : 320, padding: 16, borderLeftWidth: 1, borderColor: theme.borderColorLight }}>
                  <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ flexGrow: 1 }}>
                    <Label style={{ color: theme.text.titleLight, marginBottom: 16 }}>{t('remote-v2-map-select')}</Label>
                    {selectedBranch && <View style={{ borderRadius: 8, overflow: 'hidden' }}>{branchChoice(selectedBranch)}</View>}
                  </ScrollView>
                  <CustomButton
                    title={t('remote-v2-use-pos')}
                    disabled={!selectedBranch || !isRemoteBranchSelectable(selectedBranch)}
                    onPress={() => setMapExpanded(false)}
                    style={{ marginTop: 16, flexShrink: 0 }}
                  />
                </View>
              </View>
            </View>
          </View>
        </Modal>
      )}
      {isCompact && !!selectedMissionId && (
        <NavigationButton
          style={styles.newVersion['detailsWrapper.backButton']}
          icon={faCircleChevronLeft}
          iconSize={24}
          labelVariant={LabelType.titleSmall}
          onPress={() => setSelectedMissionId(undefined)}
          text={t('back')}
        />
      )}
      <View style={[styles.containerBase, { paddingBottom: isCompact ? 32 : 0 }, acceptanceReview.isOpen && { flex: 1, minHeight: 0 }]}>
        {(!isCompact || !selectedMissionId) && list}
        {(!isCompact || !!selectedMissionId) && (
          detail ??
          (!isCompact && (
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 }}>
              <Label>{t('remote-v2-select-mission')}</Label>
            </View>
          ))
        )}
      </View>
    </AuthGuard>
  );
};

export default RemoteMissions;
